function isNullish(value) {
    return value === null || value === undefined || value === '';
}

if (!customElements.get('gift-dispatcher')) {
    customElements.define(
        'gift-dispatcher',
        class GiftDispatcher extends HTMLElement {
            constructor() {
                super();
                this.gifts = this.querySelectorAll('input[type="hidden"]');
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer') || document.querySelector('deployer-cart-drawer');
                this.cartURLs = {
                    add: `/cart/add.js`,
                    change: `/cart/change.js`,
                    update: `/cart/update.js`,
                    cart: `/cart.json`,
                };
                this.limitedVariantIds = [];
                this.maxQuantity = 1;

                // Flags pour éviter les boucles et les appels concurrents
                this.isProcessing = false;
                this.isInternalUpdate = false;
                this.debounceTimer = null;
                this.debounceDelay = 300;

                // Bound handlers pour pouvoir les retirer
                this.boundHandleCartRefresh = this.handleCartRefresh.bind(this);
                this.boundHandleCartAdd = this.handleCartAdd.bind(this);
                this.boundHandleCartChange = this.handleCartChange.bind(this);

                this.activateFetchInterception();
            }

            connectedCallback() {
                if (window.deployerAdapter.enable_gift_dispatcher === true) {
                    this.initializeLimitedVariantIds();
                    this.listenEvents();
                }
            }

            disconnectedCallback() {
                this.removeEvents();
            }

            initializeLimitedVariantIds() {
                this.gifts.forEach(gift => {
                    if (!isNullish(gift.dataset.variantId)) {
                        this.limitedVariantIds.push(parseInt(gift.dataset.variantId));
                    }
                });
            }

            activateFetchInterception() {
                const originalFetch = window.fetch;
                const instance = this;

                window.fetch = async function (input, init) {
                    try {
                        const url = typeof input === 'string' ? input : input.url;
                        const method = init?.method || 'GET';

                        if (
                            url.includes('/cart/add.js') ||
                            url.includes('/cart/change.js') ||
                            url.includes('/cart/update.js')
                        ) {
                            const body = init?.body ? JSON.parse(init.body) : null;
                            if (body?.items) {
                                for (const item of body.items) {
                                    if (instance.limitedVariantIds.includes(item.id)) {
                                        if (url.includes('/cart/add.js') && item.quantity > instance.maxQuantity) {
                                            console.warn(
                                                `Tentative d'ajouter plus de ${instance.maxQuantity} unités du produit ${item.id}. Quantité ajustée à 0.`
                                            );
                                            item.quantity = 0;
                                        } else if (item.quantity > instance.maxQuantity) {
                                            console.warn(
                                                `Tentative de modification à plus de ${instance.maxQuantity} unités du produit ${item.id}. Quantité limitée à ${instance.maxQuantity}.`
                                            );
                                            item.quantity = instance.maxQuantity;
                                        }
                                    }
                                }
                            }
                            if (body?.id && instance.limitedVariantIds.includes(body.id)) {
                                if (url.includes('/cart/add.js') && body.quantity > instance.maxQuantity) {
                                    console.warn(
                                        `Tentative d'ajouter plus de ${instance.maxQuantity} unités du produit ${body.id}. Quantité ajustée à 0.`
                                    );
                                    body.quantity = 0;
                                } else if (body.quantity > instance.maxQuantity) {
                                    console.warn(
                                        `Tentative de modification à plus de ${instance.maxQuantity} unités du produit ${body.id}. Quantité limitée à ${instance.maxQuantity}.`
                                    );
                                    body.quantity = instance.maxQuantity;
                                }
                            }
                            init.body = JSON.stringify(body);
                        }
                        return originalFetch(input, init);
                    } catch (error) {
                        console.error('Erreur dans l\'interception de fetch:', error);
                        return originalFetch(input, init);
                    }
                };
            }

            listenEvents() {
                document.addEventListener('deployer-cart-drawer:refresh', this.boundHandleCartRefresh);
                document.addEventListener('cart-product:add', this.boundHandleCartAdd);
                document.addEventListener('cart-product:change', this.boundHandleCartChange);
            }

            removeEvents() {
                document.removeEventListener('deployer-cart-drawer:refresh', this.boundHandleCartRefresh);
                document.removeEventListener('cart-product:add', this.boundHandleCartAdd);
                document.removeEventListener('cart-product:change', this.boundHandleCartChange);

                if (this.debounceTimer) {
                    clearTimeout(this.debounceTimer);
                    this.debounceTimer = null;
                }
            }

            handleCartRefresh() {
                // Ignorer si c'est notre propre mise à jour pour éviter la boucle infinie
                if (this.isInternalUpdate) return;
                this.debouncedValidate();
            }

            handleCartAdd() {
                this.debouncedValidate();
            }

            handleCartChange() {
                // Ignorer si c'est notre propre mise à jour
                if (this.isInternalUpdate) return;
                this.debouncedValidate();
            }

            debouncedValidate() {
                // Annuler le timer précédent
                if (this.debounceTimer) {
                    clearTimeout(this.debounceTimer);
                }

                this.debounceTimer = setTimeout(() => {
                    this.processGiftValidation();
                }, this.debounceDelay);
            }

            async processGiftValidation() {
                // Éviter les appels concurrents
                if (this.isProcessing) return;

                this.isProcessing = true;

                try {
                    const cart = await this.getCart();
                    if (cart) {
                        await this.validateConditions(cart);
                    }
                } catch (error) {
                    console.error('Erreur lors de la validation des cadeaux:', error);
                } finally {
                    this.isProcessing = false;
                }
            }

            async getCart() {
                return fetch(this.cartURLs.cart, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })
                    .then(response => response.json())
                    .then(response => {
                        return response;
                    })
                    .catch((error) => {
                        console.error('Error:', error);
                    });
            }

            async validateConditions(cart) {
                // Ne pas ajouter de produits gratuits si le panier ne contient que des cartes cadeaux
                if (this.isCartOnlyGiftCards(cart)) {
                    // Collecter tous les variant IDs des cadeaux présents dans le panier
                    const giftsToRemove = [];
                    for (const [variantId] of this.groupGiftsByVariant()) {
                        if (cart.items.some(item => item.variant_id == variantId)) {
                            giftsToRemove.push(variantId);
                        }
                    }
                    // Retirer tous les cadeaux en une seule requête
                    if (giftsToRemove.length > 0) {
                        await this.removeMultipleGifts(giftsToRemove);
                    }
                    return;
                }

                // Grouper les gifts par variant ID pour gérer les conflits A/B test
                const giftsByVariant = this.groupGiftsByVariant();

                // Collecter les actions à effectuer
                const giftsToAdd = [];
                const giftsToRemove = [];
                
                for (const [variantId, gifts] of giftsByVariant) {
                    // Déterminer quel gift doit être traité en priorité
                    const priorityGift = this.getPriorityGift(gifts);
                    
                    if (!priorityGift) {
                        // Aucun gift valide pour ce variant, retirer le produit s'il est dans le panier
                        if (cart.items.some(item => item.variant_id == variantId)) {
                            giftsToRemove.push(variantId);
                        }
                        continue;
                    }

                    const tierType = priorityGift.dataset.tierType;
                    const isSufficient = this.isTierSatisfied(cart, tierType, priorityGift);

                    const restrictedItem = cart.items.find(item => item.id.toString() == variantId);
                    if (restrictedItem && restrictedItem.quantity > 1) {
                        await this.limitProductQuantity(cart, restrictedItem.id);
                        // Recharger le panier après la limitation
                        cart = await this.getCart();
                    }

                    if (isSufficient === false) {
                        if (cart.items.some(item => item.variant_id == variantId)) {
                            giftsToRemove.push(variantId);
                        }
                        continue;
                    }

                    if (!this.isAlreadyOnCart(cart, variantId)) {
                        giftsToAdd.push(variantId);
                    }
                }

                // Exécuter les suppressions en une seule requête
                if (giftsToRemove.length > 0) {
                    await this.removeMultipleGifts(giftsToRemove);
                }

                // Exécuter les ajouts en une seule requête
                if (giftsToAdd.length > 0) {
                    await this.addMultipleGifts(giftsToAdd);
                }
            }

            async limitProductQuantity(cart, productId) {
                const lineIndex = cart.items.findIndex(item => item.variant_id == productId);
                if (lineIndex === -1) return;

                try {
                    const response = await fetch(this.cartURLs.change, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            line: lineIndex + 1,
                            quantity: 1,
                        }),
                    });
                    await response.json();
                    this.updateCart();
                } catch (error) {
                    console.error('Erreur lors de la mise à jour de la quantité :', error);
                }
            }

            groupGiftsByVariant() {
                const giftsByVariant = new Map();
                
                this.gifts.forEach(gift => {
                    if (isNullish(gift.dataset.variantId)) return;
                    
                    const variantId = parseInt(gift.dataset.variantId);
                    if (!giftsByVariant.has(variantId)) {
                        giftsByVariant.set(variantId, []);
                    }
                    giftsByVariant.get(variantId).push(gift);
                });
                
                return giftsByVariant;
            }

            isCartOnlyGiftCards(cart) {
                if (!cart.items || cart.items.length === 0) return false;
                
                // Filtrer les items pour exclure les produits cadeaux (ceux gérés par le gift dispatcher)
                const nonGiftItems = cart.items.filter(item => !this.limitedVariantIds.includes(item.variant_id));
                
                // Si après filtrage il n'y a plus d'items, le panier ne contenait que des cadeaux
                if (nonGiftItems.length === 0) return true;
                
                // Vérifier si tous les items restants sont des cartes cadeaux
                return nonGiftItems.every(item => item.gift_card === true);
            }

            getPriorityGift(gifts) {
                // Filtrer d'abord les gifts valides selon la logique A/B/C/D/E test
                const validGifts = gifts.filter(gift => this.isABTestValid(gift));
                
                if (validGifts.length === 0) {
                    return null; // Aucun gift valide
                }
                
                if (validGifts.length === 1) {
                    return validGifts[0]; // Un seul gift valide
                }
                
                // Si plusieurs gifts valides, prioriser par identificateur
                // L'ordre de priorité suit l'ordre alphabétique des variantes
                const priorityOrder = ['testingE', 'testingD', 'testingC', 'testingB', 'testingA'];
                
                const sortedGifts = validGifts.sort((a, b) => {
                    const aTesting = a.dataset.testing || '';
                    const bTesting = b.dataset.testing || '';
                    
                    const aPriority = priorityOrder.indexOf(aTesting);
                    const bPriority = priorityOrder.indexOf(bTesting);
                    
                    // Les gifts sans identificateur de test ont la priorité la plus basse
                    if (aPriority === -1 && bPriority === -1) return 0;
                    if (aPriority === -1) return 1;
                    if (bPriority === -1) return -1;
                    
                    return aPriority - bPriority;
                });
                
                return sortedGifts[0];
            }

            isABTestValid(gift) {
                // Si ce n'est pas un produit testé, on l'ajoute normalement
                if (isNullish(gift.dataset.testing) || gift.dataset.testing === 'false') {
                    return true;
                }

                const testingIdentifier = gift.dataset.testing;
                const activeVariant = window.giftTestingVariant;

                // Si aucune variante n'est définie, ne pas afficher les produits en test
                if (isNullish(activeVariant)) {
                    return false;
                }

                // Le gift est valide si son identificateur correspond à la variante active
                // Exemple : si activeVariant = 'C', seuls les gifts avec testing='testingC' sont affichés
                const expectedIdentifier = `testing${activeVariant.toUpperCase()}`;
                
                return testingIdentifier === expectedIdentifier;
            }

            isTierSatisfied(cart, type, gift) {
                switch (type) {
                    case 'cart_items_count':
                        return GiftDispatcherOnCartItemsCount.validateConditions(cart, gift);
                    case 'cart_amount':
                        return GiftDispatcherOnCartAmount.validateConditions(cart, gift);
                    case 'product_on_cart':
                        return GiftDispatcherOnProductInCart.validateConditions(cart, gift);
                    default:
                        return false;
                }
            }

            isAlreadyOnCart(cart, variantId) {
                return cart.items.some((item) => item.variant_id == variantId);
            }

            updateCart() {
                // Marquer comme mise à jour interne pour éviter la boucle
                this.isInternalUpdate = true;
                document.dispatchEvent(new CustomEvent('deployer-cart-drawer:refresh'));
                
                // Réinitialiser le flag après un court délai
                setTimeout(() => {
                    this.isInternalUpdate = false;
                }, 500);
            }

            async addToCartGift(variantId) {
                const bodyConfig = {
                    items: [{ id: variantId, quantity: 1 }],
                };

                if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
                    bodyConfig.sections = this.cart.getSectionsToRender().map((section) => section.id);
                    bodyConfig.sections_url = window.location.pathname;
                }

                try {
                    const response = await fetch(this.cartURLs.add, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(bodyConfig),
                    });
                    await response.json();
                    this.updateCart();
                } catch (error) {
                    console.error('Error adding gift to cart:', error);
                }
            }

            async addMultipleGifts(variantIds) {
                const items = variantIds.map(id => ({ id, quantity: 1 }));
                const bodyConfig = { items };

                if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
                    bodyConfig.sections = this.cart.getSectionsToRender().map((section) => section.id);
                    bodyConfig.sections_url = window.location.pathname;
                }

                try {
                    const response = await fetch(this.cartURLs.add, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(bodyConfig),
                    });
                    await response.json();
                    this.updateCart();
                } catch (error) {
                    console.error('Error adding multiple gifts to cart:', error);
                }
            }

            async removeGift(cart, variantId) {
                const lineIndex = cart.items.findIndex(item => item.variant_id == variantId);
                if (lineIndex === -1) return;

                try {
                    const response = await fetch(this.cartURLs.change, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            line: lineIndex + 1,
                            quantity: 0,
                        }),
                    });
                    await response.json();
                    this.updateCart();
                } catch (error) {
                    console.error('Error removing gift from cart:', error);
                }
            }

            async removeMultipleGifts(variantIds) {
                const updates = {};
                for (const variantId of variantIds) {
                    updates[variantId] = 0;
                }

                try {
                    const response = await fetch(this.cartURLs.update, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ updates }),
                    });
                    await response.json();
                    this.updateCart();
                } catch (error) {
                    console.error('Error removing multiple gifts from cart:', error);
                }
            }
        }
    )
}


var GiftDispatcherOnCartAmount = class {
    static isCartAmountSufficient(cart, cartAmount) {
        const cartTotal = cart.items_subtotal_price;
        return cartTotal >= cartAmount;
    }

    static validateConditions(cart, gift) {
        if (isNullish(gift.dataset.cartAmount)) return false;

        const cartAmount = parseFloat(gift.dataset.cartAmount);
        const isSufficient = this.isCartAmountSufficient(cart, cartAmount);
        const isAvailable = gift.dataset.variantAvailable === "true";

        if (!isSufficient) {
            // console.warn(`Tier is not sufficient for adding gift ${variantId}`);
            return false;
        }

        if (!isAvailable) {
            // console.warn(`Gift ${variantId} is not available`);
            return false;
        }

        return true;
    }
}

var GiftDispatcherOnCartItemsCount = class {
    static isCartItemsCountSufficient(cart, cartItemsCount) {
        const cartItems = cart.item_count;
        return cartItems >= cartItemsCount;
    }

    static validateConditions(cart, gift) {
        if (isNullish(gift.dataset.cartItemsCount)) return false;

        const cartItemsCount = parseInt(gift.dataset.cartItemsCount);
        const isSufficient = this.isCartItemsCountSufficient(cart, cartItemsCount);
        const isAvailable = gift.dataset.variantAvailable === "true";

        if (!isSufficient) {
            // console.warn(`Tier is not sufficient for adding gift ${variantId}`);
            return false;
        }

        if (!isAvailable) {
            // console.warn(`Gift ${variantId} is not available`);
            return false;
        }

        return true;
    }
}

var GiftDispatcherOnProductInCart = class {
    static isProductDispatcherOnCart(cart, productId) {
        return cart.items.some((item) => item.product_id == productId);
    }

    static validateConditions(cart, gift) {
        if (isNullish(gift.dataset.variantId)) {
            // console.warn("Variant ID is not defined");
            return false;
        }

        const isAvailable = gift.dataset.variantAvailable === "true";
        const productDispatcherId = parseInt(gift.dataset.productDispatcherId);
        const isProductDispatcherOnCart = this.isProductDispatcherOnCart(cart, productDispatcherId);

        if (!isProductDispatcherOnCart) {
            // console.warn(`Dispatcher is not on cart`);
            return false;
        }

        if (!isAvailable) {
            // console.warn(`Gift ${variantId} is not available`);
            return false;
        }

        return true;
    }
}